import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslate } from 'react-admin';
import Konva from 'konva';
import {
  findIndexWithTickets,
  parseLayoutJson,
  stringToColour,
  createSeatShape,
  createSeatLabel,
  createAreaShape,
  createShadowRect,
  createSelectionRect,
  createGridLayer,
  getNextLabelNumber,
  snapToGrid,
  handleScrollContainer,
} from '../utils/seatingUtils';
import { SEATING_CONSTANTS, SEATING_COLORS } from '../constants/seatingConstants';

export const useSeatingCanvas = ({
  record,
  isAssignMode,
  fullLayout,
  isViewOnly,
  isLayoutEdit,
  dataProvider,
  tickets,
  ticketsID,
  isCreateMode,
  layoutType,
  fullLayoutID,
  setValue,
}) => {
  const translate = useTranslate();
  const [selectedShapes, setSelectedShapes] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedAreaProducts, setSelectedAreaProducts] = useState([]);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({
    width: SEATING_CONSTANTS.WIDTH,
    height: SEATING_CONSTANTS.HEIGHT,
  });
  const stageRef = useRef(null);
  const layerRef = useRef(null);
  const shadowRectRef = useRef(null);

  const applyStageSize = (stage, stageJSON) => {
    const attrsW = Number(stageJSON?.attrs?.width) || 0;
    const attrsH = Number(stageJSON?.attrs?.height) || 0;
    let maxX = attrsW;
    let maxY = attrsH;

    try {
      const groups = stage.find('Group') || [];
      groups.forEach((group) => {
        const box = group.getClientRect({ skipTransform: false });
        maxX = Math.max(maxX, box.x + box.width);
        maxY = Math.max(maxY, box.y + box.height);
      });
      // Blok yazilari Text node — bounds'a dahil et
      const texts = stage.find('Text') || [];
      texts.forEach((text) => {
        const box = text.getClientRect({ skipTransform: false });
        maxX = Math.max(maxX, box.x + box.width);
        maxY = Math.max(maxY, box.y + box.height);
      });
    } catch (e) {
      // keep attrs fallback
    }

    let width = Math.ceil(maxX) + 20;
    let height = Math.ceil(maxY) + 40;
    if (attrsW || attrsH) {
      width = Math.max(width, attrsW);
      height = Math.max(height, attrsH);
    } else {
      // Bos yeni layout
      width = Math.max(width, SEATING_CONSTANTS.WIDTH);
      height = Math.max(height, SEATING_CONSTANTS.HEIGHT);
    }
    stage.width(width);
    stage.height(height);
    setCanvasSize({ width, height });
    return { width, height };
  };

  const [useTickets, setUseTickets] = useState(tickets);
  const fieldName = isCreateMode || isLayoutEdit ? 'layout' : 'layoutJSON';
  const setField = useCallback(
    (value) => {
      if (value == null) {
        setValue(fieldName, null);
        return;
      }
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      setValue(fieldName, serialized, { shouldDirty: true });
    },
    [setValue, fieldName],
  );

  const bindEditableGroupDrag = (group) => {
    group.draggable(true);
    group.off('dragstart');
    group.off('dragmove');
    group.off('dragend');
    group.on('dragstart', () => {
      if (shadowRectRef.current) {
        shadowRectRef.current.show();
        shadowRectRef.current.moveToTop();
      }
      group.moveToTop();
    });
    group.on('dragmove', (e) => {
      if (shadowRectRef.current) {
        shadowRectRef.current.position({
          x: snapToGrid(group.x()),
          y: snapToGrid(group.y()),
        });
      }
      stageRef.current?.batchDraw();
      const scrollContainer = document.getElementById('scrollContainer');
      handleScrollContainer(e, scrollContainer);
    });
    group.on('dragend', () => {
      group.position({
        x: snapToGrid(group.x()),
        y: snapToGrid(group.y()),
      });
      stageRef.current?.batchDraw();
      shadowRectRef.current?.hide();
      if (stageRef.current) setField(stageRef.current.toJSON());
    });
  };

  const bringMetaLabelsToTop = (stage) => {
    if (!stage) return;
    try {
      const nodes = [...(stage.find('Rect') || []), ...(stage.find('Text') || [])];
      nodes.forEach((node) => {
        const n = node.getAttr('name');
        if (
          n === 'blockLabel' ||
          n === 'blockHint' ||
          n === 'krokiFooter' ||
          n === 'blockLabelBg'
        ) {
          node.moveToTop();
        }
      });
      stage.batchDraw();
    } catch (e) {
      // ignore
    }
  };

  // Update tickets when the prop changes
  useEffect(() => {
    if (tickets) {
      setUseTickets(tickets);
    }
  }, [tickets]);

  useEffect(() => {
    if (isLayoutEdit && record?.layout) {
      setTimeout(() => {
        loadLayoutLocal(record.layout, { editable: true });
        addShadowRect();
        addSelectionTool();
        setSelectedShapes([]);
      }, 300);
    } else if (isViewOnly && record) {
      loadLayoutLocal(record.layout);
    } else if (isAssignMode && record) {
      if (fullLayout) {
        setTimeout(() => {
          loadLayoutLocal(record);
          addSelectionTool();
          setSelectedShapes([]);
        }, 500);
      } else if (fullLayoutID) {
        (async () => {
          await loadLayoutJSONByID(record);
          const ticketsData = await loadTicketsByID(ticketsID);
          setUseTickets(ticketsData);
          setTimeout(() => {
            addSelectionTool();
            setSelectedShapes([]);
          }, 250);
        })();
      } else {
        (async () => {
          await loadLayout(record);
          setTimeout(() => {
            addSelectionTool();
            setSelectedShapes([]);
          }, 250);
        })();
      }
    } else {
      addGridLayer();
      addSelectionTool();
      addShadowRect();
    }
  }, [record, isAssignMode, fullLayout, isViewOnly, isLayoutEdit]);

  const loadLayout = async (layoutId) => {
    const { data } = await dataProvider.getOne('layouts', { id: layoutId });
    if (data && data.layout) {
      let stageJSON = parseLayoutJson(data.layout);
      stageRef.current = Konva.Node.create(stageJSON, 'container');
      applyStageSize(stageRef.current, stageJSON);
      layerRef.current = stageRef.current.find('Layer')[findIndexWithTickets(stageJSON)];
      layerRef.current.find('Group').forEach((group) => group.draggable(false));
      if (layerRef.current.find('Image').length > 0) {
        let url = layerRef.current.find('Image')[0].attrs.source;
        if (url && url !== '') {
          addBackgroundImageForDisplay(url);
        }
      }
      stageRef.current.draw();
      setField(stageRef.current.toJSON());
      setLayoutLoaded(true);
      bringMetaLabelsToTop(stageRef.current);
    }
  };

  const loadLayoutJSONByID = async (layoutID) => {
    const { data } = await dataProvider.getOne('eventLayouts', { id: layoutID });
    if (data && data.layoutJSON) {
      let stageJSON = parseLayoutJson(data.layoutJSON);
      stageRef.current = Konva.Node.create(stageJSON, 'container');
      applyStageSize(stageRef.current, stageJSON);
      layerRef.current = stageRef.current.find('Layer')[findIndexWithTickets(stageJSON)];
      layerRef.current.find('Group').forEach((group) => group.draggable(false));
      if (layerRef.current.find('Image').length > 0) {
        let url = layerRef.current.find('Image')[0].attrs.source;
        if (url && url !== '') {
          // addBackgroundImageForDisplay(url);
        }
      }
      stageRef.current.draw();
      setField(stageRef.current.toJSON());
      setLayoutLoaded(true);
      bringMetaLabelsToTop(stageRef.current);
    }
  };

  const loadTicketsByID = async (ticketsID) => {
    const { data } = await dataProvider.getOne('stocks', { id: ticketsID });
    if (data && data.stock) {
      return data.stock;
    }
  };

  const loadLayoutLocal = (layout, { editable = false } = {}) => {
    if (layout) {
      let stageJSON = parseLayoutJson(layout);
      stageRef.current = Konva.Node.create(stageJSON, 'container');
      applyStageSize(stageRef.current, stageJSON);
      layerRef.current = stageRef.current.find('Layer')[findIndexWithTickets(stageJSON)];
      layerRef.current.find('Group').forEach((group) => {
        if (editable) {
          bindEditableGroupDrag(group);
        } else {
          group.draggable(false);
        }
      });
      if (layerRef.current.find('Image').length > 0) {
        let url = layerRef.current.find('Image')[0].attrs.source;
        if (url && url !== '') {
          addBackgroundImageForDisplay(url);
        }
      }
      stageRef.current.draw();
      // Assign/edit: forma yaz ki Save layoutJSON'u gorsun
      setField(stageRef.current.toJSON());
      setLayoutLoaded(true);
      bringMetaLabelsToTop(stageRef.current);
    }
  };

  const addGridLayer = () => {
    const gridLayer = createGridLayer(SEATING_CONSTANTS.WIDTH, SEATING_CONSTANTS.HEIGHT);
    if (stageRef.current) {
      stageRef.current.add(gridLayer);
    }
  };

  const addShadowRect = () => {
    const shadowRectangle = createShadowRect();
    shadowRectangle.hide();
    if (layerRef.current) {
      layerRef.current.add(shadowRectangle);
      shadowRectRef.current = shadowRectangle;
    }
  };

  const addSelectionTool = () => {
    let selectionRect;
    let stage = stageRef.current;
    let layer = layerRef.current;

    if (!stage || !layer) return;

    // Remove existing event listeners to prevent duplicates
    stage.off('mousedown');
    stage.off('mousemove');
    stage.off('mouseup');

    stage.on('mousedown', (e) => {
      if (selectionRect) {
        selectionRect.destroy();
        selectionRect = null;
      }

      // Reset all seats to default colors
      const groups = layer.getChildren((node) => node.getClassName() === 'Group');
      const seats = groups.filter((rect) => rect.getClassName() === 'Group');
      seats.forEach((seat) => {
        if (seat.attrs.ticket) {
          seat.children[0].fill(stringToColour(seat.attrs.ticket));
          seat.children[1].fill('white');
        } else if (seat.attrs.tickets) {
          seat.children[0].fill('green');
        } else {
          seat.children[0].fill(layoutType === 'seating' ? 'white' : 'transparent');
          seat.children[1].fill('black');
        }
      });

      // Apply selected seats styling
      const selectedSeats = selectedShapes;
      selectedSeats.forEach((seat) => {
        if (seat && seat.attrs && (seat.attrs.ticket || seat.attrs.tickets)) {
          if (seat.attrs.ticket) {
            seat.children[0].fill(stringToColour(seat.attrs.ticket));
            if (
              seat.children[0].attrs.prevBGColor &&
              seat.children[0].attrs.prevBGColor.length !== 0 &&
              !isAssignMode
            ) {
              seat.children[0].fill(seat.children[0].attrs.prevBGColor);
              seat.children[0].setAttr('prevBGColor', null);
            } else {
              seat.children[1].fill('white');
            }
          }
          if (seat.attrs.tickets) {
            seat.children[0].fill('green');
          }
        } else {
          if (
            seat.children[0].attrs.prevBGColor &&
            seat.children[0].attrs.prevBGColor.length !== 0
          ) {
            if (
              seat.children[0].attrs.prevBGColor === 'white' ||
              seat.children[0].attrs.prevBGColor === '#FFF' ||
              seat.children[0].attrs.prevBGColor === 'transparent'
            ) {
              seat.children[0].fill(seat.children[0].attrs.prevBGColor);
              if (seat.children[0].attrs.prevBGColor === 'transparent') {
                seat.children[0].setAttr('prevBGColor', 'transparent');
              } else {
                seat.children[0].setAttr('prevBGColor', null);
              }
            }
          } else {
            seat.children[0].fill('#FFF');
          }
        }
        if (seat.attrs.bgColor) {
          seat.children[0].fill(seat.attrs.bgColor);
        }
      });

      selectionRect = createSelectionRect(e.evt.offsetX, e.evt.offsetY);
      layer.add(selectionRect);
    });

    stage.on('mousemove', (e) => {
      if (selectionRect) {
        selectionRect.width(e.evt.offsetX - selectionRect.x());
        selectionRect.height(e.evt.offsetY - selectionRect.y());
        layer.batchDraw();
      }
    });

    stage.on('mouseup', (e) => {
      if (selectionRect) {
        const groups = layer.getChildren((node) => node.getClassName() === 'Group');
        const seats = groups.filter(
          (rect) =>
            !rect.attrs.decor &&
            Konva.Util.haveIntersection(rect.getClientRect(), selectionRect.getClientRect()),
        );
        seats.forEach((seat) => {
          const prevBG = seat.children[0].fill();
          seat.children[0].setAttr('prevBGColor', prevBG);
          setTimeout(() => {
            seat.children[0].fill(SEATING_COLORS.SELECTION);
          }, 150);
        });
        setSelectedShapes(seats);
        selectionRect.destroy();
        layer.batchDraw();
        setField(stage.toJSON());
      }
    });
  };

  const addBackgroundImageForDisplay = (url) => {
    const image = new window.Image();
    image.src = url;
    const layer = new Konva.Layer();
    image.onload = () => {
      const konvaImage = new Konva.Image({
        image: image,
        x: 0,
        y: 0,
        width: image.width,
        height: image.height,
      });
      layer.add(konvaImage);
      layer.draw();
      layer.setZIndex(-10);
      stageRef.current.add(layer);
      layerRef.current.moveToTop();
    };
  };

  const addShape = (event) => {
    event.preventDefault();
    let lastX = 0;
    let lastY = 0;
    const lastGroup = layerRef.current.find('Group').pop();
    if (lastGroup) {
      lastX = lastGroup.x();
      lastY = lastGroup.y();
    }

    const labelText = getNextLabelNumber(layerRef);
    const label = createSeatLabel(labelText);
    const shape = createSeatShape();
    const seatGroup = new Konva.Group({
      x: lastX + SEATING_CONSTANTS.BLOCK_SNAP_SIZE,
      y: lastY,
      draggable: true,
    });

    seatGroup.add(shape);
    seatGroup.add(label);

    seatGroup.on('dragstart', (e) => {
      shadowRectRef.current.show();
      shadowRectRef.current.moveToTop();
      seatGroup.moveToTop();
    });

    seatGroup.on('dragmove', (e) => {
      shadowRectRef.current.position({
        x: snapToGrid(seatGroup.x()),
        y: snapToGrid(seatGroup.y()),
      });
      stageRef.current.batchDraw();
      const scrollContainer = document.getElementById('scrollContainer');
      handleScrollContainer(e, scrollContainer);
    });

    seatGroup.on('dragend', (e) => {
      seatGroup.position({
        x: snapToGrid(seatGroup.x()),
        y: snapToGrid(seatGroup.y()),
      });
      stageRef.current.batchDraw();
      shadowRectRef.current.hide();
      setField(stageRef.current.toJSON());
    });

    layerRef.current.add(seatGroup);
    layerRef.current.draw();
    setField(stageRef.current.toJSON());
    return false;
  };

  const addArea = (event, type) => {
    event.preventDefault();
    let size = prompt(translate('seating.prompt_area_width'), '35');
    let heightRectangle = null;
    if (size === null || size === '') return false;
    size = parseInt(size);
    if (type === 'rectangle') {
      heightRectangle = prompt(translate('seating.prompt_area_height'), '35');
      if (heightRectangle === null || heightRectangle === '') return false;
      heightRectangle = parseInt(heightRectangle);
    }

    let lastX = 50;
    let lastY = 50;
    const lastGroup = layerRef.current.find('Group').pop();
    if (lastGroup) {
      lastX = lastGroup.x();
      lastY = lastGroup.y();
    }

    const shape = createAreaShape(type, size, heightRectangle);
    const labelText = getNextLabelNumber(layerRef);
    const label = createSeatLabel(labelText);
    const areaGroup = new Konva.Group({
      x: lastX + size,
      y: lastY,
      draggable: true,
    });

    areaGroup.add(shape);
    areaGroup.add(label);
    areaGroup.setAttr('selectable', true);
    layerRef.current.add(areaGroup);
    layerRef.current.draw();
    setField(stageRef.current.toJSON());
    return false;
  };

  const addBackgroundImage = (event) => {
    event.preventDefault();
    let url = prompt(translate('seating.prompt_image_url'), '');
    if (url === null || url === '') return false;
    const image = new window.Image();
    image.src = url;
    image.onload = () => {
      const konvaImage = new Konva.Image({
        image: image,
        x: 0,
        y: 0,
        width: image.width,
        height: image.height,
      });
      konvaImage.setAttr('source', url);
      layerRef.current.add(konvaImage);
      layerRef.current.draw();
      setField(stageRef.current.toJSON());
    };
    return false;
  };

  const removeBackgroundImage = (event) => {
    event.preventDefault();
    const image = layerRef.current.find('Image').pop();
    if (image) {
      image.destroy();
      layerRef.current.draw();
      setField(stageRef.current.toJSON());
    }
    setSelectedShapes([]);
  };

  const deleteShapes = (shapes) => {
    shapes.forEach((shape) => shape.destroy());
    layerRef.current.draw();
    setSelectedShapes([]);
    setField(stageRef.current.toJSON());
  };

  const cloneShape = (shape) => {
    const label = window.prompt(
      translate('seating.prompt_clone_label'),
      shape.children[1].attrs.text,
    );
    if (label === null) return;
    const shapes = layerRef.current.find('Group');
    const labels = shapes.map((shape) => shape.children[1].attrs.text);
    if (labels.includes(label)) {
      alert(translate('seating.label_exists'));
      return;
    }
    const clonedShape = shape.clone({
      x: shape.x() + 10,
      y: shape.y() + 10,
    });
    clonedShape.children[1].text(label);
    layerRef.current.add(clonedShape);
    setSelectedShapes([clonedShape]);
    layerRef.current.draw();
  };

  const changeWidthHeight = (shapes) => {
    const RectShape = shapes[0].find('Rect')[0];
    const CircleShape = shapes[0].find('Circle')[0];
    if (RectShape) {
      const newWidth = window.prompt(
        translate('seating.prompt_new_width'),
        RectShape.width().toString(),
      );
      const newHeight = window.prompt(
        translate('seating.prompt_new_height'),
        RectShape.height().toString(),
      );
      if (newWidth !== null && newHeight !== null) {
        RectShape.width(parseInt(newWidth));
        RectShape.height(parseInt(newHeight));
        layerRef.current.draw();
      }
    }
    if (CircleShape) {
      const newRadius = window.prompt(
        translate('seating.prompt_new_radius'),
        CircleShape.radius().toString(),
      );
      if (newRadius !== null) {
        CircleShape.radius(parseInt(newRadius));
        layerRef.current.draw();
      }
    }
  };

  const renameLabel = (shape) => {
    const newText = window.prompt(
      translate('seating.prompt_seat_id'),
      shape.children[1].attrs.text,
    );
    if (newText !== null) {
      const existingLabel = layerRef.current
        .find('Text')
        .find((label) => label.attrs.text === newText);
      if (existingLabel) {
        alert(translate('seating.id_unique'));
      } else {
        shape.children[1].text(newText);
        layerRef.current.draw();
        setField(stageRef.current.toJSON());
      }
    }
  };

  const changeBgColor = (shapes) => {
    const color = window.prompt(
      translate('seating.prompt_color'),
      shapes[0].children[0].attrs.fill,
    );
    if (color !== null) {
      shapes.forEach((shape) => {
        shape.children[0].fill(color);
        shape.setAttr('bgColor', color);
      });
      layerRef.current.draw();
    }
  };

  const changeFgColor = (shapes) => {
    const color = window.prompt(
      translate('seating.prompt_color'),
      shapes[0].children[1].attrs.fill,
    );
    if (color !== null) {
      shapes.forEach((shape) => {
        shape.children[1].fill(color);
        shape.setAttr('fgColor', color);
      });
      layerRef.current.draw();
    }
  };

  const assignTicket = (shapes) => {
    if (selectedTicket) {
      const ticket = useTickets.find((ticket) => ticket.id === selectedTicket);
      shapes.forEach((shape) => {
        shape.setAttr('ticket', ticket.id);
        shape.children[0].fill(stringToColour(ticket.id));
        shape.children[1].fill('white');
      });
      layerRef.current.draw();
      setSelectedTicket(null);
      setField(stageRef.current.toJSON());
    } else {
      alert(translate('seating.select_product_first'));
    }
  };

  const assignProductsToArea = (shapes) => {
    if (selectedAreaProducts.length > 0) {
      shapes.forEach((shape) => {
        shape.setAttr('tickets', JSON.stringify(selectedAreaProducts));
        shape.children[0].fill('green');
      });
      layerRef.current.draw();
      setSelectedAreaProducts([]);
      setField(stageRef.current.toJSON());
    } else {
      alert(translate('seating.select_product_first'));
    }
  };

  return {
    selectedShapes,
    setSelectedShapes,
    selectedTicket,
    setSelectedTicket,
    selectedAreaProducts,
    setSelectedAreaProducts,
    layoutLoaded,
    canvasSize,
    stageRef,
    layerRef,
    shadowRectRef,
    useTickets,
    setUseTickets,
    setField,
    handleAddShape: addShape,
    handleAddArea: addArea,
    handleAddBackgroundImage: addBackgroundImage,
    handleRemoveBackgroundImage: removeBackgroundImage,
    handleDeleteShapes: deleteShapes,
    handleCloneShape: cloneShape,
    handleChangeWidthHeight: changeWidthHeight,
    handleRenameLabel: renameLabel,
    handleChangeBgColor: changeBgColor,
    handleChangeFgColor: changeFgColor,
    handleAssignTicket: assignTicket,
    handleAssignProductsToArea: assignProductsToArea,
    handleLoadLayout: loadLayout,
    handleLoadLayoutJSONByID: loadLayoutJSONByID,
    handleLoadTicketsByID: loadTicketsByID,
    handleLoadLayoutLocal: loadLayoutLocal,
    handleAddGridLayer: addGridLayer,
    handleAddShadowRect: addShadowRect,
    handleAddSelectionTool: addSelectionTool,
    handleAddBackgroundImageForDisplay: addBackgroundImageForDisplay,
  };
};
